import { useEffect, useMemo, useRef, useState } from "react";
import { buildGalleryPlan, } from "../api/gallery/galleryPlan";
import { GALLERY_STORAGE_KEYS } from "../gallery/keys";
import { GALLERY_PLAN_DEBOUNCE_MS, GALLERY_PLAN_LIMITS, planFieldsOf, planKeyOf, planValuesOf, } from "../gallery/planner";
import { storage } from "../storage";
const savedPlan = () => {
    try {
        const raw = storage.get(GALLERY_STORAGE_KEYS.plan);
        const plan = raw ? JSON.parse(raw) : {};
        // One saved before the order was stored rebuilds from its input instead.
        return plan.orderedIds ? plan : undefined;
    }
    catch {
        return undefined;
    }
};
/** The planner's last inputs, from an earlier visit; none when cleared or unreadable. */
const savedPlanInput = () => {
    try {
        const raw = storage.get(GALLERY_STORAGE_KEYS.plannerInput);
        return raw ? JSON.parse(raw) : undefined;
    }
    catch {
        return undefined;
    }
};
/**
 * The Gallery Planner's plan. Builds can overlap (one per settled field), so
 * only the latest one's answer lands.
 *
 * A `preview` build lands in `draft` instead, leaving the plan alone until
 * `applyDraft` (the app's planner sheet: try limits, then Apply). `draft` is
 * undefined with nothing pending, null for a pending clear.
 */
export const useGalleryPlan = (snapshot) => {
    const [plan, setPlan] = useState(savedPlan);
    const [draft, setDraft] = useState();
    const [building, setBuilding] = useState(false);
    const [error, setError] = useState(false);
    const [empty, setEmpty] = useState(false);
    // Bumped on clear, so hosts can remount the fields empty.
    const [fieldsKey, setFieldsKey] = useState(0);
    const buildSeq = useRef(0);
    const snapshotRef = useRef(snapshot);
    snapshotRef.current = snapshot;
    // Open signed out too: the backend then plans on the market solutions.
    const build = async (input, { preview = false } = {}) => {
        const seq = ++buildSeq.current;
        // A preview lands in the draft (null: nothing to show), a build in the plan.
        const land = (next) => preview ? setDraft(next ?? null) : setPlan(next);
        if (!preview) {
            storage.set(GALLERY_STORAGE_KEYS.plannerInput, JSON.stringify(input));
            storage.remove(GALLERY_STORAGE_KEYS.plan);
        }
        setBuilding(true);
        setError(false);
        setEmpty(false);
        try {
            const result = await buildGalleryPlan(input);
            if (seq !== buildSeq.current)
                return;
            if (result.sets.length) {
                const built = {
                    ...result,
                    input,
                    ...snapshotRef.current(new Map(result.sets.map((s) => [s.setId, s.grade]))),
                };
                if (!preview)
                    storage.set(GALLERY_STORAGE_KEYS.plan, JSON.stringify(built));
                land(built);
            }
            else {
                land(undefined);
                setEmpty(true);
            }
        }
        catch {
            // The old plan (or draft) goes too, so the error is all that shows.
            if (seq === buildSeq.current) {
                land(undefined);
                setError(true);
            }
        }
        finally {
            if (seq === buildSeq.current)
                setBuilding(false);
        }
    };
    // Also drops a build still in flight, so its answer can't bring the plan back.
    const clear = () => {
        // Before the remount below, so the fields come back empty.
        storage.remove(GALLERY_STORAGE_KEYS.plannerInput);
        storage.remove(GALLERY_STORAGE_KEYS.plan);
        setFieldsKey((k) => k + 1);
        buildSeq.current++;
        setBuilding(false);
        setError(false);
        setEmpty(false);
        setPlan(undefined);
        setDraft(undefined);
    };
    /** The draft becomes the plan: built, saved and filtering the list; a null draft clears it. */
    const applyDraft = () => {
        if (draft === undefined)
            return;
        if (draft === null)
            return clear();
        storage.set(GALLERY_STORAGE_KEYS.plannerInput, JSON.stringify(draft.input));
        storage.set(GALLERY_STORAGE_KEYS.plan, JSON.stringify(draft));
        setPlan(draft);
        setDraft(undefined);
        setEmpty(false);
        setError(false);
    };
    /** Every field emptied in preview: applying will clear the plan. */
    const clearDraft = () => {
        buildSeq.current++;
        setBuilding(false);
        setError(false);
        setEmpty(false);
        setDraft(null);
    };
    /** Drops the draft and puts the fields back to the plan's. */
    const discardDraft = () => {
        buildSeq.current++;
        setBuilding(false);
        setError(false);
        setEmpty(false);
        setDraft(undefined);
        setFieldsKey((k) => k + 1);
    };
    // One Map per plan, so hosts can memoize on it.
    const plannedGrades = useMemo(() => plan ? new Map(plan.sets.map((p) => [p.setId, p.grade])) : undefined, [plan]);
    return {
        plan,
        building,
        error,
        empty,
        fieldsKey,
        build,
        clear,
        plannedGrades,
        draft,
        applyDraft,
        clearDraft,
        discardDraft,
    };
};
/**
 * The planner's fields: any change re-solves once typing settles, an empty
 * field sets no limit and all fields empty clears the plan. Without a plan they
 * start from the last visit's inputs, which then rebuild like a fresh edit.
 *
 * `manual` waits for `submit` instead (the app's Build plan button);
 * `resetKey` changing puts the fields back to the plan's.
 */
export const useGalleryPlanFields = ({ plan, categoryId, onBuild, onClear, manual = false, resetKey, }) => {
    const [fields, setFields] = useState(() => planFieldsOf(plan?.input ?? savedPlanInput()));
    const values = planValuesOf(fields);
    // The limits the last build went out with, so only a real change rebuilds.
    // No plan: empty limits on this category, so an idle planner stays idle and
    // fields restored from the last visit rebuild once.
    const lastBuilt = useRef(planKeyOf(plan?.input ?? {}, plan ? plan.input.categoryId : categoryId));
    // A saved plan that loads after mount fills the fields.
    useEffect(() => {
        if (!plan)
            return;
        const key = planKeyOf(plan.input, plan.input.categoryId);
        if (key === lastBuilt.current)
            return;
        lastBuilt.current = key;
        setFields(planFieldsOf(plan.input));
    }, [plan]);
    const firstReset = useRef(resetKey);
    useEffect(() => {
        if (resetKey === firstReset.current)
            return;
        firstReset.current = resetKey;
        lastBuilt.current = planKeyOf(plan?.input ?? {}, plan ? plan.input.categoryId : categoryId);
        setFields(planFieldsOf(plan?.input ?? savedPlanInput()));
        // Only a new key resets; the plan it resets to is read as it is then.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey]);
    const key = planKeyOf(values, categoryId);
    const blank = GALLERY_PLAN_LIMITS.every((k) => values[k] === undefined);
    const submit = () => {
        lastBuilt.current = key;
        if (blank)
            onClear();
        else
            onBuild({ categoryId, ...values });
    };
    useEffect(() => {
        if (manual || key === lastBuilt.current)
            return;
        const timer = setTimeout(submit, GALLERY_PLAN_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // values and the callbacks follow key; only a change rebuilds.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return {
        fields,
        setField: (limit, text) => setFields((f) => ({ ...f, [limit]: text })),
        submit,
        /** The fields differ from what the last build used. */
        dirty: key !== lastBuilt.current,
        blank,
    };
};
//# sourceMappingURL=useGalleryPlan.js.map